import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, ShieldCheck } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

export const AiEncryption = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const [message, setMessage] = useState("Gorilla Mart Secret Launch Codes");
    const [encrypted, setEncrypted] = useState("");
    const [algorithm, setAlgorithm] = useState<'caesar' | 'aes' | 'quantum-safe'>('caesar');

    // Breaking progress
    const [attacker, setAttacker] = useState<'cpu' | 'ai' | 'quantum'>('cpu');
    const [progress, setProgress] = useState(0);
    const [isBreaking, setIsBreaking] = useState(false);
    const [cracked, setCracked] = useState(false);

    // scramble logic
    const encrypt = (algo: string) => {
        if (algo === 'caesar') {
            setEncrypted("Jqtnnc Octv Ugetgv Ncwpej Eqfgu");
        } else if (algo === 'aes') {
            setEncrypted("U2FsdGVkX19+q6Xj8/7+q6Xj8/7+q6Xj8/7+");
        } else {
            setEncrypted("🎲⚛️⚡🔒 [QUANTUM ENTANGLED DATA] 🔒⚡⚛️🎲");
        }
        setCracked(false);
        setProgress(0);
        setIsBreaking(false);
    };

    useEffect(() => {
        encrypt(algorithm);
    }, [algorithm]);

    const startAttack = (type: 'cpu' | 'ai' | 'quantum') => {
        setAttacker(type);
        setIsBreaking(true);
        setProgress(0);
        setCracked(false);
    };

    useEffect(() => {
        if (!isBreaking) return;

        let speed = 0.5; // Base speed
        if (attacker === 'ai') speed = 2; // AI is faster at finding patterns
        if (attacker === 'quantum') speed = 10; // Quantum is instant

        // Difficulty modifier
        let difficulty = 1;
        if (algorithm === 'aes') difficulty = 5;
        if (algorithm === 'quantum-safe') difficulty = 100; // Impossible for classical/AI

        if (attacker === 'quantum' && algorithm !== 'quantum-safe') difficulty = 0.1; // Quantum breaks AES
        if (attacker === 'quantum' && algorithm === 'quantum-safe') difficulty = 50; // Quantum Safe resists Quantum

        const interval = setInterval(() => {
            setProgress(p => {
                const step = (speed / difficulty) * 5; // Adjustment
                const newP = p + step;
                if (newP >= 100) {
                    clearInterval(interval);
                    setCracked(true);
                    setIsBreaking(false);
                    return 100;
                }
                return newP;
            });
        }, 50);

        return () => clearInterval(interval);
    }, [isBreaking, attacker, algorithm]);

    // Check win condition: User must find a combination that AI cannot break easily, 
    // OR understand the concept. 
    // Let's make it a lesson: "Upgrade to Quantum Safe" to win against AI+Quantum future.

    const handleWin = () => {
        if (algorithm === 'quantum-safe' && !completed) {
            if (attacker === 'ai') {
                // If they withstood AI, that's good enough for now?
                // Or maybe just clicking through the lesson concludes it.
            }
        }
    };

    // Auto-complete if they select Quantum Safe and try to attack it
    useEffect(() => {
        if (algorithm === 'quantum-safe' && progress < 10 && !isBreaking && attacker === 'ai' && !completed) {
            // They saw AI fail? No, progress is slow.
        }

        // If they use Quantum Safe and "Quantum" attacker fails (or is very slow), they win.
        if (algorithm === 'quantum-safe' && isBreaking && attacker === 'quantum') {
            // Let it run a bit then win?
        }
    }, [algorithm, isBreaking, attacker]);

    useEffect(() => {
        if (progress > 20 && algorithm === 'quantum-safe' && attacker === 'quantum' && !completed) {
            setCompleted(true);
            completeModule('ai-encryption', 100);
            setShowSuccess(true);
        }
    }, [progress]);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pt-24">
            <div className="max-w-4xl mx-auto p-8">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-black font-comic text-purple-500 mb-4 flex items-center justify-center gap-3">
                        <Brain size={40} /> AI vs Encryption code
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        AI pattern recognition can crack simple codes. Quantum computers can crack complex ones. Can you stay safe?
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Defender Side */}
                    <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700">
                        <h3 className="font-bold text-blue-400 mb-4 flex items-center gap-2">
                            <ShieldCheck /> Defense (Encryption)
                        </h3>

                        <div className="space-y-4">
                            <button
                                onClick={() => setAlgorithm('caesar')}
                                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${algorithm === 'caesar' ? 'border-blue-500 bg-blue-900/20' : 'border-slate-700 hover:border-slate-600'}`}
                            >
                                <div className="font-bold">1. Basic Cipher (Caesar)</div>
                                <div className="text-xs text-slate-400">Vulnerable to Patterns</div>
                            </button>
                            <button
                                onClick={() => setAlgorithm('aes')}
                                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${algorithm === 'aes' ? 'border-blue-500 bg-blue-900/20' : 'border-slate-700 hover:border-slate-600'}`}
                            >
                                <div className="font-bold">2. Standard Encryption (AES)</div>
                                <div className="text-xs text-slate-400">Safe from AI, Vulnerable to Quantum</div>
                            </button>
                            <button
                                onClick={() => setAlgorithm('quantum-safe')}
                                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${algorithm === 'quantum-safe' ? 'border-purple-500 bg-purple-900/20' : 'border-slate-700 hover:border-slate-600'}`}
                            >
                                <div className="font-bold">3. Quantum-Safe (Lattice)</div>
                                <div className="text-xs text-slate-400">The Future Standard</div>
                            </button>
                        </div>

                        <div className="mt-8 bg-slate-900 p-4 rounded-xl font-mono text-sm break-all text-center">
                            {cracked ? <span className="text-red-500">{message}</span> : <span className="text-green-500">{encrypted}</span>}
                        </div>
                    </div>

                    {/* Attacker Side */}
                    <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700">
                        <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                            <Zap /> Attacker Method
                        </h3>

                        <div className="grid grid-cols-3 gap-2 mb-6">
                            <button onClick={() => startAttack('cpu')} disabled={isBreaking} className="bg-slate-700 p-2 rounded-lg text-xs font-bold hover:bg-slate-600">
                                Classic CPU
                            </button>
                            <button onClick={() => startAttack('ai')} disabled={isBreaking} className="bg-red-900/30 border border-red-500/50 p-2 rounded-lg text-xs font-bold hover:bg-red-900/50 text-red-300">
                                AI Model
                            </button>
                            <button onClick={() => startAttack('quantum')} disabled={isBreaking} className="bg-purple-900/30 border border-purple-500/50 p-2 rounded-lg text-xs font-bold hover:bg-purple-900/50 text-purple-300">
                                Quantum Comp
                            </button>
                        </div>

                        <div className="relative h-64 bg-slate-900 rounded-xl overflow-hidden flex items-end justify-center p-4 border border-slate-700">
                            {/* Matrix Rain Effect Placeholder */}
                            <div className="absolute inset-0 opacity-20 bg-[url('https://media.giphy.com/media/dummy/giphy.gif')]"></div>

                            <motion.div
                                className="w-full bg-red-500 rounded-t-xl relative overflow-hidden"
                                initial={{ height: '0%' }}
                                animate={{ height: `${progress}%` }}
                                transition={{ type: 'tween', ease: 'linear', duration: 0.1 }}
                            >
                                <div className="absolute top-0 w-full text-center text-xs font-bold text-white mt-1">
                                    {isBreaking ? 'CRACKING...' : cracked ? 'BREACHED' : 'READY'}
                                </div>
                            </motion.div>
                        </div>

                        <div className="mt-4 text-sm text-slate-400">
                            {attacker === 'cpu' && "Standard brute force. Slow against AES."}
                            {attacker === 'ai' && "AI spots patterns in simple ciphers instantly."}
                            {attacker === 'quantum' && "Quantum computers can factor large numbers, breaking RSA/AES."}
                        </div>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <ModuleSuccess
                    title="Future Proofed!"
                    onRetry={() => setShowSuccess(false)}
                >
                    <div className="space-y-4">
                        <p className="text-slate-600">
                            You upgraded to Quantum-Safe encryption!
                        </p>
                        <div className="bg-purple-100 p-4 rounded-xl border border-purple-200">
                            <h4 className="font-bold text-purple-800 text-sm mb-1">Lesson: The Arms Race</h4>
                            <p className="text-purple-700 text-xs">
                                As attackers get better tools (AI, Quantum), defenders must invent new math (Lattice-based cryptography) to stay secure.
                            </p>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};
